import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    inheritedHeader: (isFolder?: boolean) => string | React.ReactNode;
    directHeader: (isFolder?: boolean) => string | React.ReactNode;
    buttonLabel: (Icon: any, count: any) => string | React.ReactNode;
    inviteButtonLabel: string;
    accessListHeader: {
        /** Header showing folder user count */
        folder: (count: number) => string | React.ReactNode;
        /** Header showing project user count */
        project: (count: number) => string | React.ReactNode;
        /** Header showing workspace user count */
        workspace: (count: number) => string | React.ReactNode;
    };
    accessCategoryIconAriaLabel: {
        /** Aria label for folder access icon */
        folder: string;
        /** Aria label for project access icon */
        project: string;
        /** Aria label for workspace access icon */
        workspace: string;
    };
    memberRemoved: (resourceName: string, memberName: string) => string | React.ReactNode;
    spinnerLabel: string;
    undo: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Header for subgroup list of members with inherited/folder access to a resource. */
            inheritedHeader: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (isFolder: boolean = false) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.inheritedHeader?c=59y9u493wl6j", defaultMessage: "{isFolder, select, true {Folder Access} other {Inherited access}}" }, { isFolder: isFolder }));
                },
                enumerable: true,
                configurable: true
            },
            /** Header for subgroup list of members with direct access to a resource. */
            directHeader: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (isFolder: boolean = false) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.directHeader?c=2iuicxys54qnh", defaultMessage: "{isFolder, select, true {Direct members} other {Direct access}}" }, { isFolder: isFolder }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for members button showing icon and count */
            buttonLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (Icon: any, count: any) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.buttonLabel?c=2lexynlh6xm3a", defaultMessage: "<jsx>Members {Icon} {count}</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), Icon: Icon, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for invite users button */
            inviteButtonLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.inviteButtonLabel?c=2lp1gr824iryy", defaultMessage: "Invite users" }));
                },
                enumerable: true,
                configurable: true
            },
            accessListHeader: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Header showing folder user count */
                        folder: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessListHeader.folder?c=1jzm7hkp9xvox", defaultMessage: "Folder users ({count})" }, { count: count }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Header showing project user count */
                        project: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessListHeader.project?c=27o7walwdhalu", defaultMessage: "Project users ({count})" }, { count: count }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Header showing workspace user count */
                        workspace: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessListHeader.workspace?c=3dd6jne7g7n9m", defaultMessage: "Workspace users ({count})" }, { count: count }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            accessCategoryIconAriaLabel: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Aria label for folder access icon */
                        folder: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessCategoryIconAriaLabel.folder?c=f8z6h4z5qq8w", defaultMessage: "Folder" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Aria label for project access icon */
                        project: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessCategoryIconAriaLabel.project?c=1y2semxo3c9fl", defaultMessage: "Project" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Aria label for workspace access icon */
                        workspace: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.accessCategoryIconAriaLabel.workspace?c=fvis4ig6qtg3", defaultMessage: "Workspace" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            /** Toast message confirming member removal with names */
            memberRemoved: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceName: string, memberName: string) => __intl__.intl().formatMessage({ id: "InviteAccessMembers.memberRemoved?c=2t2z99pot1a7", defaultMessage: "{memberName} was removed from {resourceName}" }, { memberName: memberName, resourceName: resourceName }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for search loading spinner */
            spinnerLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.spinnerLabel?c=2xzqohvscs9ht", defaultMessage: "Searching" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for undo button in toast */
            undo: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMembers.undo?c=n4og3f213zda", defaultMessage: "Undo" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
